import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DropPlaceholder } from '@form/FormDropzone';
import { MAX_UPLOAD_BYTES, MAX_UPLOAD_LABEL } from '@form/FormDropzone/constants';
import { Button, Progress } from '@ui';
import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { cn } from '~/lib/cn';
import { PendingUpload } from './types';

interface Props {
    name: string;
    pending: PendingUpload[];
    onFiles: (files: File[]) => void;
    onDismiss: (uid: string) => void;
    /** react-dropzone v11 takes MIME types as a string list. */
    accept?: string[];
    className?: string;
}

export const FileUploadZone = ({ name, pending, onFiles, onDismiss, accept, className }: Props) => {
    const { t } = useTranslation('form');
    const [rejected, setRejected] = useState<string>();

    const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
        accept,
        maxSize: MAX_UPLOAD_BYTES,
        multiple: true,
        onDrop: (accepted) => {
            setRejected(undefined);
            if (accepted.length) onFiles(accepted);
        },
        onDropRejected: (rejections) =>
            setRejected(
                rejections.some((rejection) => rejection.errors.some((error) => error.code === 'file-too-large'))
                    ? t('dropzone.tooLarge', { size: MAX_UPLOAD_LABEL })
                    : t('dropzone.rejected')
            )
    });

    return (
        <div className={cn('tw-flex tw-flex-col tw-gap-2', className)}>
            <div {...getRootProps()} className="tw-cursor-pointer">
                <input {...getInputProps()} />
                <DropPlaceholder name={name} isDragActive={isDragActive} isDragReject={isDragReject} />
            </div>
            {rejected && <span className="tw-text-xs tw-font-semibold tw-text-error">{rejected}</span>}
            {pending.map((entry) => (
                <div
                    key={entry.uid}
                    className="tw-flex tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-border-accents-2 tw-px-3 tw-py-2"
                >
                    <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1.5">
                        <span className="tw-truncate tw-text-sm">{entry.name}</span>
                        {entry.error ? (
                            <span className="tw-text-xs tw-text-error">{entry.error}</span>
                        ) : (
                            <Progress size="sm" color="success" progress={entry.progress} />
                        )}
                    </div>
                    {entry.error && (
                        <Button size="xs" flat color="light" onPress={() => onDismiss(entry.uid)}>
                            <FontAwesomeIcon icon={faTimes} />
                        </Button>
                    )}
                </div>
            ))}
        </div>
    );
};

export default FileUploadZone;
